import { isString } from '@egovsolutions/type-checking';
import { HttpParams } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject } from '@angular/core';
import { Subject, merge, Observable, throwError } from 'rxjs';
import { catchError, switchMap, map } from 'rxjs/operators';

const ADDRESS_FORMATTING_DEFAULT_OPTIONS = {
    includeName: true,
    skipName: null,
    lineSeparator: '\n',
    inlineSeparator: ', ',
    stateAsCode: true,
    skipCountryIfUS: true,
};
function formatZipCode(zip) {
    if (zip.length === 9)
        return `${zip.substr(0, 5)}-${zip.substr(5)}`;
    else
        return zip;
}
function formatAddress(address, options) {
    const mergedOptions = {
        ...ADDRESS_FORMATTING_DEFAULT_OPTIONS,
        ...options
    };
    let lines = [];
    if (mergedOptions.includeName
        &&
            address.name
        &&
            address.name.toUpperCase() !== mergedOptions.skipName?.toUpperCase())
        lines.push(address.name);
    const addressLines = [];
    if (address.addressLine1)
        addressLines.push(address.addressLine1);
    if (address.addressLine2)
        addressLines.push(address.addressLine2);
    lines = lines.concat(addressLines);
    if (address.city || address.state || address.zip) {
        let cityStateZipLine = '';
        if (address.city) {
            cityStateZipLine += address.city;
            if (address.state || address.zip)
                cityStateZipLine += mergedOptions.inlineSeparator;
        }
        if (address.state) {
            cityStateZipLine += address.state;
            if (address.zip)
                cityStateZipLine += ' ';
        }
        if (address.zip)
            cityStateZipLine += formatZipCode(address.zip);
        lines.push(cityStateZipLine);
    }
    if (address.country
        &&
            (!mergedOptions.skipCountryIfUS
                ||
                    address.countryIsUS === undefined
                ||
                    address.countryIsUS === false))
        lines.push(address.country);
    return lines.join(mergedOptions.lineSeparator);
}

// Error types
var EgovAPIErrorType;
(function (EgovAPIErrorType) {
    EgovAPIErrorType["AGENT"] = "agent";
    EgovAPIErrorType["CLIENT"] = "client";
    EgovAPIErrorType["NOT_FOUND"] = "not-found";
    EgovAPIErrorType["SERVER"] = "server";
    EgovAPIErrorType["OTHER"] = "other";
})(EgovAPIErrorType || (EgovAPIErrorType = {}));
function createEgovAPIError(messageOrValues) {
    let error = {
        type: EgovAPIErrorType.OTHER,
        permanent: true,
    };
    if (messageOrValues) {
        error = isString(messageOrValues)
            ? { ...error, message: messageOrValues }
            : { ...error, ...messageOrValues };
    }
    return error;
}
function createEgovAPIAgentError(messageOrValues) {
    return {
        message: 'Error trying to contact server.',
        ...createEgovAPIError(messageOrValues),
        type: EgovAPIErrorType.AGENT,
        permanent: false
    };
}
// The request happened but something went wrong with it
function createEgovAPIRequestError(values) {
    return {
        message: 'Something went wrong connecting to the server.',
        ...createEgovAPIError(values),
    };
}
function createEgovAPIServerError(values) {
    return {
        ...createEgovAPIRequestError(values),
        type: EgovAPIErrorType.SERVER,
    };
}
function createEgovAPIClientError(values) {
    return {
        ...createEgovAPIRequestError(values),
        type: EgovAPIErrorType.CLIENT,
    };
}
function createEgovAPINotFoundError(values) {
    return {
        ...createEgovAPIClientError(values),
        type: EgovAPIErrorType.NOT_FOUND,
    };
}
// Error type checkers
const isEgovAPIError = (value) => {
    return Object.values(EgovAPIErrorType).includes(value.type)
        && (value.permanent === true || value.permanent === false);
};
// Convert HttpErrorResponse into HttpErrorData
function extractResponseData(response) {
    return {
        response: response.body,
        headers: response.headers,
        ok: response.ok,
        redirected: response.redirected,
        status: response.status,
        statusText: response.statusText,
        responseType: response.type,
        url: response.url,
    };
}

class EgovAPIResponses {
    constructor() {
        this._successResponses$ = new Subject();
        this.successResponses$ = this._successResponses$.asObservable();
        this._errorResponses$ = new Subject();
        this.errorResponses$ = this._errorResponses$.asObservable();
        this.responses$ = merge(this._successResponses$, this._errorResponses$);
    }
    broadcastSuccess(response) { this._successResponses$.next(response); return response; }
    broadcastError(response) { this._errorResponses$.next(response); return response; }
}
EgovAPIResponses.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: EgovAPIResponses, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
EgovAPIResponses.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: EgovAPIResponses, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: EgovAPIResponses, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const SIMULATE_RESPONSE_EXCEPTIONS = new InjectionToken('Simulate response exceptions');
const HOST_BASE = new InjectionToken('Server host base');
const API_PUBLIC_KEY = new InjectionToken('API public key');
class EgovAPI {
    constructor(
    // Dependencies
    hostBase, publicKey, responses) {
        this.hostBase = hostBase;
        this.publicKey = publicKey;
        this.responses = responses;
        // Request
        this.apiBase = this.hostBase + 'api/v2/';
        this.bearer = null;
    }
    request$(options) {
        // Method and URL
        const method = options.method || options.bodyParameters && 'POST' || 'GET';
        // Headers
        const contentType = options.body ? 'application/json; charset=utf-8' : 'application/x-www-form-urlencoded';
        let authorizationHeader = `Basic ${this.publicKey}`;
        const includeBearer = !!this.bearer;
        if (includeBearer)
            authorizationHeader += `, Bearer ${this.bearer}`;
        const headers = {
            'Content-Type': contentType,
            'Authorization': authorizationHeader,
        };
        // URL
        let urlString = options.endpoint;
        if (options.urlFields)
            for (const name in options.urlFields)
                urlString = urlString.split(`{{${name}}}`).join(encodeURIComponent(options.urlFields[name]));
        const url = new URL(urlString, this.apiBase);
        if (method === 'GET' || method === 'DELETE')
            url.searchParams.set('detail_level', options.detail || 'public');
        if (options.urlParameters)
            for (const name in options.urlParameters)
                url.searchParams.set(name, options.urlParameters[name]);
        // Body
        let body = null;
        if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
            if (options.body)
                body = JSON.stringify(options.body);
            else {
                let bodyParams = new HttpParams();
                if (method === 'POST' || method === 'PUT' || method === 'PATCH')
                    bodyParams = bodyParams.set('detail_level', options.detail || 'public');
                if (options.bodyParameters)
                    for (const name in options.bodyParameters)
                        bodyParams = bodyParams.set(name, options.bodyParameters[name]);
                body = bodyParams.toString();
            }
        }
        // Request
        const request$ = new Observable((subscriber) => {
            const controller = new AbortController();
            fetch(url.toString(), { method, headers, body, signal: controller.signal })
                .then(response => subscriber.next(response))
                .catch(e => subscriber.error(e));
            return () => controller.abort();
        }).pipe(
        // Catch errors
        catchError((error) => {
            if (error instanceof ErrorEvent || error instanceof ProgressEvent)
                return throwError(() => createEgovAPIAgentError({
                    response: error
                }));
            else if (error instanceof TypeError)
                return throwError(() => createEgovAPIRequestError({
                    response: error,
                    permanent: false,
                }));
            else if (error.status.toString().startsWith('5'))
                return throwError(() => createEgovAPIServerError(extractResponseData(error)));
            else if (error.status === 404)
                return throwError(() => createEgovAPINotFoundError(extractResponseData(error)));
            else if (error.status.toString().startsWith('4'))
                return throwError(() => createEgovAPIClientError(extractResponseData(error)));
            else
                return throwError(() => ({
                    ...createEgovAPIRequestError(extractResponseData(error)),
                    message: 'Unknown error contacting server.',
                    permanent: false,
                }));
        }), 
        // Get JSON body
        switchMap(response => response.json()), 
        // Cast errors into proper error types and broadcast responses
        map(response => {
            if (Object.prototype.hasOwnProperty.call(response, 'status') && response.status === 1)
                return this.responses.broadcastSuccess(response);
            else
                throw createEgovAPIError({
                    message: 'Something didn\'t work as expected',
                    response: this.responses.broadcastError(response),
                    permanent: true
                });
        }));
        return request$;
    }
}
EgovAPI.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: EgovAPI, deps: [{ token: HOST_BASE }, { token: API_PUBLIC_KEY }, { token: EgovAPIResponses }], target: i0.ɵɵFactoryTarget.Injectable });
EgovAPI.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: EgovAPI, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: EgovAPI, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [HOST_BASE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [API_PUBLIC_KEY]
                }] }, { type: EgovAPIResponses }]; } });

/* eslint-disable @typescript-eslint/naming-convention */
const US_STATES = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};
const US_COMMONWEALTH_TERRITORIES = {
    AS: 'American Samoa',
    DC: 'District of Columbia',
    FM: 'Federated States of Micronesia',
    GU: 'Guam',
    MH: 'Marshall Islands',
    MP: 'Northern Mariana Islands',
    PW: 'Palau',
    PR: 'Puerto Rico',
    VI: 'Virgin Islands',
};
function codeToState(stateCode) {
    return US_STATES[stateCode] || US_COMMONWEALTH_TERRITORIES[stateCode];
}

/**
 * Generated bundle index. Do not edit.
 */

export { ADDRESS_FORMATTING_DEFAULT_OPTIONS, API_PUBLIC_KEY, EgovAPI, EgovAPIErrorType, EgovAPIResponses, HOST_BASE, SIMULATE_RESPONSE_EXCEPTIONS, US_COMMONWEALTH_TERRITORIES, US_STATES, codeToState, createEgovAPIAgentError, createEgovAPIClientError, createEgovAPIError, createEgovAPINotFoundError, createEgovAPIRequestError, createEgovAPIServerError, extractResponseData, formatAddress, formatZipCode, isEgovAPIError };
